import { FormComponentFactory } from "../FormComponentFactory";
import { Component, ComponentConfig } from "../Component";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { Fragment, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import {
  useComponentConfigAtom,
  useComponentCustomOrEditConfigAtom,
  useOutputWire,
} from "../state/ComponentAtoms";
import { List, ListItem } from "@mui/material";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";

interface ListComponent extends ComponentConfig {
  datasetId?: string;
  propertyKey?: string;
}
export function registerListComponent() {
  FormComponentFactory.addComponent("List", {
    config: () => {
      return {
        connections: {
          inputs: [],
          outputs: [],
          inputConnectors: {},
          outputConnectors: {},
        },
      };
    },
    component: (config: Component) => {
      return <ListDisplay config={config} />;
    },
    editor: (config: Component) => {
      return <ListEditor componentId={config.id} />;
    },
    fixedOutputs: () => {
      return ["selected"];
    },
  });
}

function ListDisplay({ config }: { config: Component }) {
  const cache = useAtomValue(dataCacheAtom);
  const [vanillaComponentConfig] = useComponentCustomOrEditConfigAtom(
    config.id
  );
  const componentConfig = vanillaComponentConfig as ListComponent;
  /// Set up handlers for output wiring
  const selectedValueWireKey =
    "connections" in componentConfig &&
    "outputConnectors" in componentConfig.connections
      ? componentConfig.connections.outputConnectors["selected"]
      : undefined;
  const setWireValue = useOutputWire(selectedValueWireKey);
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  // Check if we have enough data to display component
  const datasetId = componentConfig.datasetId;
  if (!(!!datasetId && !!componentConfig.propertyKey)) {
    return <div>Data not found</div>;
  }
  const handleListSelect = (label: string, idx: number) => {
    setWireValue(label);
    setSelectedValue(idx);
  };
  const data = cache[datasetId];
  const propertyKey = componentConfig.propertyKey;
  ///
  let values: string[] = [];
  values = Object.values(data).map((row) => row[propertyKey]);
  return (
    <List key={"Display-" + config.id}>
      {values.map((label, idx) => {
        return (
          <ListItem
            key={idx}
            value={label}
            onClick={() => handleListSelect(label, idx)}
            selected={idx === selectedValue}
          >
            {label}
          </ListItem>
        );
      })}
    </List>
  );
}

function ListEditor({ componentId }: { componentId: string }) {
  const [config, setConfig] = useComponentConfigAtom(componentId);
  const componentConfig = config.editConfig as ListComponent;
  const datasetId = componentConfig.datasetId;
  const setDatasetId = (newDatasetId?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: { ...config.editConfig, ...{ datasetId: newDatasetId } },
        },
      };
    });
  };

  const propertyKey = useMemo(
    () => componentConfig.propertyKey,
    [componentConfig.propertyKey]
  );
  const setPropertyKey = (newPropertyKey?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: {
            ...config.editConfig,
            ...{ propertyKey: newPropertyKey },
          },
        },
      };
    });
  };
  return (
    <Fragment key={"Editor-" + config.id}>
      <div style={{ fontSize: ".5em" }}>{config.id}</div>
      <div>
        <DataSetSelector
          onSelect={setDatasetId}
          title="Dataset"
          selection={datasetId}
        />
      </div>
      <div>
        <ColumnSelector
          title="Property Key"
          datasetId={datasetId}
          selection={propertyKey}
          onChange={(value: string) => setPropertyKey(value)}
          isSingleSelection={true}
        />
      </div>
    </Fragment>
  );
}
